import { ReactNode } from "react"
import { ButtonBase, Checkbox, CheckboxProps, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    card: {
        padding: ".625rem .75rem .625rem .375rem",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".5rem",
        justifyContent: "flex-start",
        textAlign: "left",
        transition: theme.transitions.create(["border-color", "background-color"], {
            duration: theme.transitions.duration.short,
        }),
        "&:focus-visible": {
            outline: `.125rem solid ${theme.palette.primary.main}`,
            outlineOffset: ".125rem",
        },
    },
    cardChecked: {
        borderColor: theme.palette.success.main,
        background: theme.palette.tailwind.emerald[50],
    },
    checkbox: {
        flexShrink: 0,
        padding: ".375rem",
    },
}))

type Props = Readonly<{
    checked: boolean
    onChange: (checked: boolean) => void
    label: ReactNode
    sublabel?: ReactNode
    color?: CheckboxProps["color"]
    /** Tint the card when checked; use with color="success". */
    highlightChecked?: boolean
    disabled?: boolean
    className?: string
}>

/** A checkbox in a bordered card with a bold label and an optional muted explanation. The whole card is the click target. */
export function CheckboxCard({
    checked,
    onChange,
    label,
    sublabel,
    color,
    highlightChecked = false,
    disabled = false,
    className,
}: Props) {
    const { classes, cx } = useStyles()

    return (
        <ButtonBase
            component="div"
            role="checkbox"
            aria-checked={checked}
            disableRipple
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cx(classes.card, highlightChecked && checked && classes.cardChecked, className)}
        >
            <Stack direction="row" alignItems={sublabel ? "flex-start" : "center"}>
                <Checkbox
                    size="small"
                    color={color}
                    checked={checked}
                    disabled={disabled}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-hidden": true }}
                    className={classes.checkbox}
                />
                <Stack spacing=".125rem">
                    {label}
                    {sublabel}
                </Stack>
            </Stack>
        </ButtonBase>
    )
}
